// 역할 정의
export const ROLES = {
  ADMIN: 'admin',
  BEXCO: 'bexco',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

// 권한 정의
export const PERMISSIONS = {
  // 대시보드
  DASHBOARD_VIEW: [ROLES.ADMIN, ROLES.BEXCO],
  
  // 자주하는 질문
  FAQ_VIEW: [ROLES.ADMIN, ROLES.BEXCO],
  FAQ_CREATE: [ROLES.ADMIN, ROLES.BEXCO],
  FAQ_EDIT: [ROLES.ADMIN, ROLES.BEXCO],
  FAQ_DELETE: [ROLES.ADMIN, ROLES.BEXCO],
  FAQ_SYNC_CHROMADB: [ROLES.ADMIN, ROLES.BEXCO],
  
  // 쿼리 로그
  LOGS_VIEW: [ROLES.ADMIN, ROLES.BEXCO],
  LOGS_EXPORT: [ROLES.ADMIN, ROLES.BEXCO],
  
  // 파일 관리
  FILES_VIEW: [ROLES.ADMIN, ROLES.BEXCO],
  FILES_UPLOAD: [ROLES.ADMIN], // bexco는 업로드 불가
  FILES_DELETE: [ROLES.ADMIN],
  FILES_CHUNKS_VIEW: [ROLES.ADMIN, ROLES.BEXCO], // 문서 청크 조회 가능
  FILES_CRAWL: [ROLES.ADMIN, ROLES.BEXCO], // 웹 크롤링 가능
  
  // 프롬프트 규칙
  PROMPT_RULES_VIEW: [ROLES.ADMIN],
  PROMPT_RULES_CREATE: [ROLES.ADMIN],
  PROMPT_RULES_EDIT: [ROLES.ADMIN],
  PROMPT_RULES_DELETE: [ROLES.ADMIN],
  
  // 설정
  SETTINGS_VIEW: [ROLES.ADMIN, ROLES.BEXCO],
  SETTINGS_EDIT: [ROLES.ADMIN], // 시스템 설정 편집 (동의어, 패턴 등)
  SETTINGS_SYSTEM_INFO: [ROLES.ADMIN, ROLES.BEXCO], // 시스템 정보 조회
  SETTINGS_AI_CONFIG: [ROLES.ADMIN], // AI 설정 admin만
  SETTINGS_EMBEDDING_CONFIG: [ROLES.ADMIN], // 임베딩 설정 admin만
  SETTINGS_PROMPT_CONFIG: [ROLES.ADMIN], // 프롬프트 설정 admin만
  
  // 워크플로우 로그
  WORKFLOW_LOGS_VIEW: [ROLES.ADMIN], // admin만 볼 수 있음
  
  // 분석 리포트
  ANALYSIS_REPORT_VIEW: [ROLES.ADMIN], // admin만 볼 수 있음
  
  // 문의게시판
  INQUIRY_BOARD_VIEW: [ROLES.ADMIN, ROLES.BEXCO],
  INQUIRY_BOARD_CREATE: [ROLES.ADMIN, ROLES.BEXCO], // admin, bexco 모두 작성 가능
  INQUIRY_BOARD_EDIT: [ROLES.ADMIN, ROLES.BEXCO], // 조건부 (본인 또는 진행중이 아닐 때)
  INQUIRY_BOARD_DELETE: [ROLES.ADMIN, ROLES.BEXCO], // 조건부 (본인 또는 진행중이 아닐 때)
  INQUIRY_BOARD_STATUS_CHANGE: [ROLES.ADMIN], // 상태 변경은 admin만
  INQUIRY_COMMENT_CREATE: [ROLES.ADMIN, ROLES.BEXCO],
  INQUIRY_COMMENT_DELETE: [ROLES.ADMIN, ROLES.BEXCO], // 조건부 (본인 또는 관리자)
  
  // 공지사항
  ANNOUNCEMENT_VIEW: [ROLES.ADMIN, ROLES.BEXCO], // 전체 조회 가능
  ANNOUNCEMENT_CREATE: [ROLES.ADMIN], // admin만 작성 가능
  ANNOUNCEMENT_EDIT: [ROLES.ADMIN], // admin만 수정 가능
  ANNOUNCEMENT_DELETE: [ROLES.ADMIN], // admin만 삭제 가능
} as const;

/**
 * 사용자 역할에 권한이 있는지 확인
 */
export const hasPermission = (userRole: Role | null, permission: keyof typeof PERMISSIONS): boolean => {
  if (!userRole) return false;
  return PERMISSIONS[permission].includes(userRole);
};

/**
 * 여러 권한 중 하나라도 있는지 확인
 */
export const hasAnyPermission = (userRole: Role | null, permissions: (keyof typeof PERMISSIONS)[]): boolean => {
  return permissions.some(permission => hasPermission(userRole, permission));
};

/**
 * 모든 권한이 있는지 확인
 */
export const hasAllPermissions = (userRole: Role | null, permissions: (keyof typeof PERMISSIONS)[]): boolean => {
  return permissions.every(permission => hasPermission(userRole, permission));
};

